import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { RoomAtAGlanceApiResponse } from 'listings/interfaces/rooms/room-at-a-glance.interface';
import { ListingsService } from 'listings/services/listings.service';
import { BehaviorSubject, catchError, combineLatest, map, of, shareReplay, switchMap } from 'rxjs';
import { ListingStatusEnum } from 'shared/constants/listing-statuses';
import { CurrentSupplierUserInterface } from 'shared/interfaces/current-supplier-user.interface';
import { AccessTypeEnum } from 'shared/interfaces/permissions.interface';
import { UserApiService } from 'shared/services';
import { supplierUserHasResourcePermission } from 'shared/util/supplierUserHasResourcePermission';

const LISTINGS_API = environment.BASE_URL + 'listingApi';
const ROOM_TYPE_FEATURE_GROUP_FIELD_NAME = 'roomType';
const ROOM_TYPES_FIELD_NAME = 'roomTypes';

@Injectable({
    providedIn: 'root',
})
export class RoomOverviewGeneralService {
    private listingsService = inject(ListingsService);
    private http = inject(HttpClient);
    private translateService = inject(TranslateService);
    private userApiService = inject(UserApiService);

    private currentUser = toSignal(this.userApiService.getLoginInformationContinuously());

    selectedRoomId = signal<string | null>(null);
    selectedRoomId$ = toObservable(this.selectedRoomId);

    listingId = signal<string | null>(null);
    listingId$ = toObservable(this.listingId);

    loadRoomOverview = signal(false);
    loadRoomOverview$ = toObservable(this.loadRoomOverview);

    private refreshRoomOverview$ = new BehaviorSubject<void>(undefined);

    roomOverviewDataTrigger$ = combineLatest([this.loadRoomOverview$, this.refreshRoomOverview$]).pipe(
        map(([loadRoomOverview]) => loadRoomOverview),
    );

    currentLang = toSignal(this.translateService.onLangChange.pipe(map((event) => event.lang)), {
        initialValue: this.translateService.getCurrentLang() ?? 'en',
    });
    private currentLang$ = toObservable(this.currentLang);

    listingData = toSignal(
        combineLatest([this.listingId$, this.loadRoomOverview$]).pipe(
            switchMap(([listingId, loadRoomOverview]) => {
                if (!listingId || !loadRoomOverview) {
                    return of(null);
                }
                return this.listingsService.getListingById(listingId).pipe(
                    catchError((error) => {
                        console.error('Failed to load listing for room overview', error);
                        return of(null);
                    }),
                );
            }),
        ),
        { initialValue: null },
    );

    hasVisualsAccess = computed<boolean | null>(() => {
        const listingData = this.listingData();
        return listingData ? (listingData.hasVisualsAccess ?? false) : null;
    });

    roomTypeFeatureGroupId$ = combineLatest([this.listingId$, this.loadRoomOverview$]).pipe(
        switchMap(([listingId, loadRoomOverview]) => {
            if (!listingId || !loadRoomOverview) {
                return of(null);
            }
            return this.listingsService.getFeatureGroups(listingId).pipe(
                map((featureGroups) => featureGroups?.find((group) => group.fieldName === ROOM_TYPE_FEATURE_GROUP_FIELD_NAME)?.id ?? null),
                catchError((error) => {
                    console.error('Failed to load room type feature group', error);
                    return of(null);
                }),
            );
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
    );

    roomTypeFeatureGroupId = toSignal<string | null>(this.roomTypeFeatureGroupId$, { initialValue: null });

    roomTypeFormStructure = toSignal(
        combineLatest([this.listingId$, this.roomTypeFeatureGroupId$]).pipe(
            switchMap(([listingId, featureGroupId]) => {
                if (!listingId || !featureGroupId) {
                    return of(null);
                }
                return this.listingsService.getFormStructure(listingId, featureGroupId).pipe(
                    catchError((error) => {
                        console.error('Failed to load room type form structure', error);
                        return of(null);
                    }),
                );
            }),
        ),
        { initialValue: null },
    );

    private roomTypeFormValues = toSignal(
        combineLatest([this.listingId$, this.roomTypeFeatureGroupId$, this.selectedRoomId$]).pipe(
            switchMap(([listingId, featureGroupId, roomId]) => {
                if (!listingId || !featureGroupId || !roomId) {
                    return of(null);
                }
                return this.listingsService.getFormValues(listingId, featureGroupId).pipe(
                    catchError((error) => {
                        console.error('Failed to load room type form values', error);
                        return of(null);
                    }),
                );
            }),
        ),
        { initialValue: null },
    );

    // Position of the selected room in the listing's room types, as used by the channel comparison paths
    selectedRoomIndex = computed<number | null>(() => {
        const roomId = this.selectedRoomId();
        const roomTypesNode = this.roomTypeFormValues()?.[ROOM_TYPE_FEATURE_GROUP_FIELD_NAME]?.[ROOM_TYPES_FIELD_NAME];
        const rooms = Array.isArray(roomTypesNode) ? roomTypesNode : roomTypesNode?.[ROOM_TYPES_FIELD_NAME];

        if (!roomId || !Array.isArray(rooms)) {
            return null;
        }

        const roomIndex = rooms.findIndex((room: any) => room?.id === roomId);
        return roomIndex >= 0 ? roomIndex : null;
    });

    atAGlanceData = toSignal<RoomAtAGlanceApiResponse | null>(
        combineLatest([this.listingId$, this.selectedRoomId$, this.currentLang$, this.roomOverviewDataTrigger$]).pipe(
            switchMap(([listingId, roomId, languageCode, loadRoomOverview]) => {
                if (!listingId || !roomId || !languageCode || !loadRoomOverview) {
                    return of(null);
                }
                return this.http
                    .get<RoomAtAGlanceApiResponse>(`${LISTINGS_API}/room-overview/general`, {
                        params: { listingId, roomId, languageCode },
                    })
                    .pipe(
                        catchError((error) => {
                            console.error('Failed to load room overview at a glance', error);
                            return of(null);
                        }),
                    );
            }),
        ),
        { initialValue: null },
    );

    // Forces the room overview widgets to refetch their data.
    refreshRoomOverview(): void {
        this.refreshRoomOverview$.next();
    }

    readonly canEditRoomType = computed(() => {
        const currentUser = this.currentUser();
        const resourceId = this.roomTypeFeatureGroupId();

        if (!currentUser || !resourceId) {
            return false;
        }

        if (currentUser.isShiji) {
            return true;
        }

        return supplierUserHasResourcePermission(
            (currentUser as CurrentSupplierUserInterface).resourcePermissions,
            resourceId,
            AccessTypeEnum.EDIT,
        );
    });

    readonly isListingStatusReadOnly = computed(() => {
        const status = this.listingData()?.listingStatus;

        return status === ListingStatusEnum.LIVE || status === ListingStatusEnum.APPROVAL;
    });

    readonly isRoomOverviewReadOnly = computed(() => this.isListingStatusReadOnly() || !this.canEditRoomType());
}
